import { useCallback } from 'react'

import { DAY_KEY_TO_ENUM } from 'components/configured-service/EditServiceRecurrenceDialog/utils/recurrenceConstants'
import { EVENT_INSTANCE_TYPES, RECURRENCE_V2 } from 'settings/constants/service'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { formatDollarsToCents } from 'utils/price'
import { isNonWeeklyRecurrence as getIsNonWeeklyRecurrence, isWeeklyRecurrence as getIsWeeklyRecurrence } from 'utils/service'

type FormOption = { id?: string | number | null }
type FormEventDay = { isSelected?: boolean; isRequired?: boolean }

type ServiceDetailsFormData = {
  endDate?: Date | null
  material?: FormOption | null
  measure?: FormOption | null
  method?: FormOption | null
  price?: string | null
  pricingPeriod?: FormOption | null
  pricingZone?: FormOption | null
  purchaseOrderNumber?: string
  billingName?: string
  useBillingName?: boolean
  recurrenceFrequency?: string
  recurrenceInterval?: string
  recurrence?: (FormOption & { recurrencePer?: string | null }) | null
  serviceEvents?: unknown[]
  serviceType?: string
  startDate?: Date | null
  deactivateOnEndDate?: boolean
  deactivate?: boolean
  taxZone?: FormOption | null
  eventInstanceType?: string | null
  dayOfTheWeek?: string | string[] | null
  eventOccurrence?: string | string[] | null
  dayOfTheMonth?: number | number[] | null
  action?: FormOption | null
  billingEndDate?: Date | null
  billingStartDate?: Date | null
  useDifferentDatesForBilling?: boolean
  businessLine?: FormOption | null
  daysOfWeek?: Record<string, FormEventDay>
}

/**
 * Formats non-weekly recurrence events that occur on specific days of the week.
 * Creates event objects by pairing days of the week with their occurrence patterns.
 *
 * @param {string|string[]} dayOfTheWeek - Day(s) of the week for the events
 * @param {string|string[]} eventOccurrence - Occurrence pattern(s) for the events (e.g., "FIRST", "SECOND")
 * @returns {Object[]} Array of event objects with dayOfTheWeek and eventOccurrence properties
 */
const formatNonWeeklyDayOfWeekEvents = (
  dayOfTheWeek: string | string[] | null | undefined,
  eventOccurrence: string | string[] | null | undefined
) => {
  const dayOfWeeks = Array.isArray(dayOfTheWeek) ? dayOfTheWeek : [dayOfTheWeek]
  const eventOccurrences = Array.isArray(eventOccurrence) ? eventOccurrence : [eventOccurrence]

  const events = []
  const maxLength = Math.max(dayOfWeeks.length, eventOccurrences.length)

  for (let i = 0; i < maxLength; i++) {
    events.push({
      dayOfTheWeek: dayOfWeeks[i] || dayOfWeeks[0],
      eventOccurrence: eventOccurrences[i] ?? eventOccurrences[0],
    })
  }

  return events
}

/**
 * Formats non-weekly recurrence events that occur on specific dates of the month.
 * Converts date values into proper event objects for API submission.
 *
 * @param {number|number[]} dayOfTheMonth - Specific day(s) of the month for the events (1-31)
 * @returns {Object[]} Array of event objects with dayOfTheMonth property
 */
const formatNonWeeklyDateEvents = (dayOfTheMonth: number | number[] | null | undefined) => {
  const dayOfMonths = Array.isArray(dayOfTheMonth) ? dayOfTheMonth : [dayOfTheMonth]
  return dayOfMonths.map(day => ({ dayOfTheMonth: day }))
}

/**
 * Formats weekly recurrence events from the days of week selection.
 * Filters selected days and maps them to the proper format with customer requirements.
 *
 * @param {Object} daysOfWeek - Object with day keys containing isSelected and isRequired properties
 * @param {boolean} daysOfWeek[dayKey].isSelected - Whether the day is selected for service
 * @param {boolean} daysOfWeek[dayKey].isRequired - Whether customer presence is required on this day
 * @returns {Object[]} Array of formatted day objects with dayOfTheWeek and customerRequired properties
 */
const formatWeeklyEvents = (daysOfWeek: Record<string, FormEventDay>) => {
  return Object.keys(daysOfWeek)
    .filter(day => daysOfWeek[day].isSelected)
    .map(day => ({
      dayOfTheWeek: DAY_KEY_TO_ENUM[day as keyof typeof DAY_KEY_TO_ENUM] || day.toUpperCase(),
      customerRequired: daysOfWeek[day].isRequired,
    }))
}

/**
 * Formats service events for API submission based on the service recurrence type and configuration.
 * Determines the appropriate formatting strategy based on weekly vs non-weekly recurrence
 * and event instance types.
 *
 * @param {Object} params - The service event parameters
 * @param {string} params.serviceType - The type of service (e.g., RECURRENCE_V2.ON_REQUEST)
 * @param {string|string[]} params.dayOfTheWeek - Day(s) of the week for non-weekly events
 * @param {string|string[]} params.eventOccurrence - Occurrence pattern(s) for non-weekly events
 * @param {number|number[]} params.dayOfTheMonth - Specific day(s) of the month for date-based events
 * @param {Object} params.daysOfWeek - Weekly recurrence day selections
 * @param {string} params.recurrenceInterval - The recurrence interval type
 * @param {string} params.eventInstanceType - Type of event instance (DAY_OF_WEEK or date-based)
 * @returns {Object[]|null} Formatted service events array or null for on-request services
 */
const formatServiceEventsForSubmission = ({
  serviceType,
  dayOfTheWeek,
  eventOccurrence,
  dayOfTheMonth,
  daysOfWeek,
  recurrenceInterval,
  eventInstanceType,
}: {
  serviceType?: string
  dayOfTheWeek?: string | string[] | null
  eventOccurrence?: string | string[] | null
  dayOfTheMonth?: number | number[] | null
  daysOfWeek?: Record<string, FormEventDay>
  recurrenceInterval?: string
  eventInstanceType?: string | null
  serviceEvents?: unknown[]
}) => {
  if (serviceType === RECURRENCE_V2.ON_REQUEST) return null

  const isWeeklyRecurrence = getIsWeeklyRecurrence(recurrenceInterval)
  const isNonWeeklyRecurrence = getIsNonWeeklyRecurrence(recurrenceInterval)
  const isDayOfWeekEventInstance = eventInstanceType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK

  if (isNonWeeklyRecurrence && isDayOfWeekEventInstance) {
    return formatNonWeeklyDayOfWeekEvents(dayOfTheWeek, eventOccurrence)
  }

  if (isNonWeeklyRecurrence) {
    return formatNonWeeklyDateEvents(dayOfTheMonth)
  }

  if (isWeeklyRecurrence) {
    return formatWeeklyEvents(daysOfWeek ?? {})
  }

  return null
}

/**
 * Custom hook for handling service details form submission formatting.
 * Provides utilities to format form data and service events for API submission.
 *
 * @param {string} serviceId - The unique identifier for the service being edited
 * @returns {Object} Object containing formatFormData function
 */
export const useServiceDetailsFormSubmission = (serviceId: string) => {
  /**
   * Formats the typed form data into the API submission payload: date formatting,
   * dollar-to-cent conversion, and ID extraction from the selected form objects.
   */
  const formatFormData = useCallback(
    (data: ServiceDetailsFormData) => {
      const {
        endDate = null,
        material = null,
        measure = null,
        method = null,
        price = '0',
        pricingPeriod = null,
        pricingZone = null,
        purchaseOrderNumber = '',
        billingName = '',
        useBillingName = false,
        recurrenceFrequency = '1',
        recurrenceInterval,
        recurrence: recurrenceObj = null,
        serviceEvents = [],
        serviceType = '',
        startDate = null,
        deactivateOnEndDate = false,
        deactivate = false,
        taxZone = null,
        eventInstanceType = null,
        dayOfTheWeek = null,
        eventOccurrence = null,
        dayOfTheMonth = null,
        action = null,
        billingEndDate = null,
        billingStartDate = null,
        useDifferentDatesForBilling = false,
        businessLine = null,
        daysOfWeek = {},
      } = data
      const materialId = material?.id ?? ''
      const measureId = measure?.id ?? ''
      const methodId = method?.id ?? ''
      const pricingPeriodId = pricingPeriod?.id ?? ''
      const pricingZoneId = pricingZone?.id ?? ''
      const recurrenceId = recurrenceObj?.id ?? ''
      const recurrencePer = recurrenceObj?.recurrencePer ?? '1'
      const taxZoneId = taxZone?.id ?? ''

      const actionId = action?.id ?? ''
      const businessLineId = businessLine?.id ?? ''

      const formattedServiceEvents = formatServiceEventsForSubmission({
        serviceType,
        serviceEvents,
        dayOfTheWeek,
        eventOccurrence,
        dayOfTheMonth,
        daysOfWeek,
        recurrenceInterval,
        eventInstanceType,
      })

      return {
        id: serviceId,
        startDate: formatDateToBEFormatDateFns(startDate),
        endDate: formatDateToBEFormatDateFns(endDate),
        billingStartDate: useDifferentDatesForBilling ? formatDateToBEFormatDateFns(billingStartDate) : null,
        billingEndDate: useDifferentDatesForBilling ? formatDateToBEFormatDateFns(billingEndDate) : null,
        servicePriceCents: formatDollarsToCents(price),
        deactivateOnEndDate,
        deactivate,

        purchaseOrderNumber,
        billingName: useBillingName && billingName.trim() ? billingName.trim() : null,
        recurrenceFrequency,

        // Include IDs from form data objects if available
        businessLineId: businessLineId || undefined,
        materialId: materialId || undefined,
        measureId: measureId || undefined,
        pricingPeriodId: pricingPeriodId || undefined,
        pricingZoneId: pricingZoneId || undefined,
        recurrenceId: recurrenceId || undefined,
        recurrenceInterval,
        recurrencePer,
        serviceActionId: actionId || undefined,
        serviceMethodId: methodId || undefined,
        taxZoneId: taxZoneId || undefined,
        recurringServiceEvents: formattedServiceEvents,
      }
    },
    [serviceId]
  )

  return { formatFormData }
}
